import { Component } from '@theme/component';
import { ComparisonSliderComponent } from '@theme/comparison-slider';

/**
 * Reuses Ritual's native comparison slider wholesale. The only override is
 * animateSlider(): the base class plays a one-time intro wiggle that ends by
 * forcing slider.value = '50', which would overwrite each card's configured
 * start position. We neutralise just that animation; all of the drag, touch,
 * pointer, keyboard and --compare sync behaviour is inherited unchanged.
 */
class NeerComparisonSlider extends ComparisonSliderComponent {
  animateSlider() {
    /* no-op: keep the per-card start position the merchant set */
  }
}

if (!customElements.get('neer-comparison-slider')) {
  customElements.define('neer-comparison-slider', NeerComparisonSlider);
}

/**
 * Carousel + "see more" controller for the neer-before-after section.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling card track.
 * @property {HTMLButtonElement} [previous] - Previous arrow.
 * @property {HTMLButtonElement} [next] - Next arrow.
 * @property {HTMLButtonElement} [more] - The mobile "see more" button.
 *
 * @extends Component<Refs>
 */
class NeerBeforeAfter extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();
    this.updateArrows = this.updateArrows.bind(this);
    this.refs.track.addEventListener('scroll', this.updateArrows, { passive: true });
    window.addEventListener('resize', this.updateArrows, { passive: true });
    this.updateArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.track?.removeEventListener('scroll', this.updateArrows);
    window.removeEventListener('resize', this.updateArrows);
  }

  /** Width of one card plus the column gap. */
  step() {
    const track = this.refs.track;
    const card = track.querySelector('[data-card]');
    if (!card) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  next() {
    this.refs.track.scrollBy({ left: this.step(), behavior: 'smooth' });
  }

  previous() {
    this.refs.track.scrollBy({ left: -this.step(), behavior: 'smooth' });
  }

  /** Reveal the cards hidden behind the mobile cap, then drop the button. */
  showMore() {
    this.classList.add('is-expanded');
    if (this.refs.more) this.refs.more.hidden = true;
  }

  /** Disable arrows at the ends of the track. */
  updateArrows() {
    const track = this.refs.track;
    const max = track.scrollWidth - track.clientWidth - 1;
    if (this.refs.previous) this.refs.previous.disabled = track.scrollLeft <= 0;
    if (this.refs.next) this.refs.next.disabled = track.scrollLeft >= max;
  }
}

if (!customElements.get('neer-before-after')) {
  customElements.define('neer-before-after', NeerBeforeAfter);
}
